<script setup>
// Fullscreen "get the app" step for mobile web visitors on member-facing pages
// (see useMobileApp for exactly when). Light theme only. Client-only so the
// server never renders it and there is no flash.
import {onMounted} from 'vue';
import {useMobileApp} from '~/composables/useMobileApp';

const {device, shouldPrompt, postJoin, openInApp, openStore, snooze, applyOnLoad} = useMobileApp();

onMounted(() => applyOnLoad());

const continueOnWeb = () => snooze();
</script>

<template>
  <client-only>
    <transition name="aho">
      <div v-if="shouldPrompt" class="aho" role="dialog" aria-modal="true" aria-label="Get the Tap Leagues app">
        <div class="aho-card">
          <div class="aho-badge">
            <img src="/favicon/web-app-manifest-192x192.png" alt="" class="aho-icon">
          </div>
          <div class="aho-eyebrow">{{ postJoin ? "You're in" : 'Tap Leagues' }}</div>
          <h1 class="aho-title">{{ postJoin ? 'Now get the app' : 'This is better in the app' }}</h1>
          <p class="aho-text">
            Challenges, results and RSVPs arrive as notifications, live scoring works courtside,
            and everything you just opened is one tap away.
          </p>

          <v-btn color="primary" variant="flat" size="large" rounded="pill" block class="text-none aho-primary"
                 prepend-icon="mdi-cellphone-arrow-down" @click="openInApp">
            {{ postJoin ? 'Open in the app' : 'Open in the app' }}
          </v-btn>
          <button type="button" class="aho-store" @click="openStore">
            <v-icon size="16">{{ device.platform === 'android' ? 'mdi-google-play' : 'mdi-apple' }}</v-icon>
            {{ device.platform === 'android' ? 'Get it on Google Play' : 'Download on the App Store' }}
          </button>

          <button type="button" class="aho-later" @click="continueOnWeb">Maybe later, continue on the web</button>
        </div>
      </div>
    </transition>
  </client-only>
</template>

<style scoped lang="scss">
.aho {
  position: fixed;
  inset: 0;
  z-index: 3100;
  background: rgb(var(--v-theme-background));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 20px calc(24px + env(safe-area-inset-bottom));
}

.aho-card {
  width: 100%;
  max-width: 420px;
  text-align: center;
}

.aho-badge {
  width: 84px;
  height: 84px;
  margin: 0 auto 18px;
  border-radius: 22px;
  background: rgb(var(--v-theme-brandTint));
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.aho-icon { width: 100%; height: 100%; object-fit: cover; }

.aho-eyebrow {
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: .8px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 6px;
}

.aho-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.15;
  margin: 0 0 10px;
}

.aho-text {
  font-size: 14.5px;
  line-height: 1.6;
  color: rgb(var(--v-theme-charcoal));
  margin: 0 0 22px;
}

.aho-primary { font-weight: 700; }

.aho-store {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 13.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
  background: none;
  border: 0;
  padding: 8px 12px;
}

.aho-later {
  display: block;
  margin: 18px auto 0;
  font-size: 13px;
  color: rgb(var(--v-theme-textSubtle));
  background: none;
  border: 0;
  padding: 8px 12px;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.aho-enter-active, .aho-leave-active { transition: opacity .2s; }
.aho-enter-from, .aho-leave-to { opacity: 0; }
</style>
